import {useState, useRef, useLayoutEffect} from 'react';
import {createConfirmedDraftAutosave} from '../shared/confirmedDraftAutosave.js';
import {useAppDraftGuard} from '../app/navigation/index.js';
import {expectedFrom, newPigRequestId, pigRejectionMessage, type PigOpOutcome} from '../lib/pigOperationsApi.js';
import type {MutableRefObject} from 'react';
import type {PigFeederGroup, PigSubBatch} from '../lib/pig.js';
import type {FeederOpRunner} from './pigFeederOps.js';
import type {PigConflictState} from './PigConflictNotice.js';

// ── Types ─────────────────────────────────────────────────────────────────

// The sub-batch form. Only `name` is always present (every setSubForm call
// supplies it); the rest depend on MODE, which is the shape this form has always
// had: new mode carries {sex, count}, edit mode carries the locked
// giltCount/boarCount/originalPigCount display fields.
export type PigSubBatchForm = {
  name: string;
  notes?: string;
  sex?: string;
  count?: number | string | null;
  giltCount?: number;
  boarCount?: number;
  originalPigCount?: number;
};

// The inline notice the view renders. Same {kind, message} pair used across the
// pig views.
export type PigNotice = {kind: string; message: string};

// setTimeout resolves to the DOM overload here (the browser project declares no
// node types), so a pending timer id is a number.
export type TimerRef = MutableRefObject<ReturnType<typeof setTimeout> | undefined>;

export type UsePigSubBatchesDeps = {
  feederGroups: PigFeederGroup[];
  setFeederGroups: (next: PigFeederGroup[]) => void;
  // The route's latest feeder list (updated synchronously by setFeederGroups
  // and by an operation's adopted rows), not this render's snapshot.
  getFeederGroups: () => PigFeederGroup[];
  applyFeederOp: FeederOpRunner;
  reread: () => void;
  setNotice: (notice: PigNotice | null) => void;
  confirmDelete: (message: string, onConfirm: () => void) => void;
  subAutoSaveTimer: TimerRef;
  pigAutoSaveTimer: TimerRef;
  partitionDirtyRef: MutableRefObject<boolean>;
};

// Every sub-batch write is ONE pig_apply_operation (migration 231) on the
// feeder document: create, update (name/notes), partition (count), archive,
// reopen, delete. The server checks the parent's remaining gilts/boars under
// the document lock, records what a delete orphans, and logs the pig.batch
// Activity in the write's own transaction — so this hook emits none.
//
// Drafts are kept until an operation is CONFIRMED. Partition edits apply to the
// route's copy at once (the editor shows them), are flushed by the shared 1.5s
// debounce or by the feeder form's close, and are sent DECREASES FIRST so a
// rebalance never over-allocates in between. Each one's expected count is the
// PERSISTED count captured at its first edit, moved only by a confirmed save.

const partitionKey = (batchId: string, subId: string) => batchId + '\u0000' + subId;
const isBoarsSub = (s: PigSubBatch) =>
  (parseInt(s.boarCount as string) || 0) > 0 && (parseInt(s.giltCount as string) || 0) === 0;
const subCount = (s: PigSubBatch) =>
  isBoarsSub(s) ? parseInt(s.boarCount as string) || 0 : parseInt(s.giltCount as string) || 0;

function failureMessage(outcome: PigOpOutcome, prefix: string): string | null {
  if (outcome.status === 'rejected') return prefix + pigRejectionMessage(outcome);
  if (outcome.status === 'unknown') return prefix + outcome.message;
  if (outcome.status === 'conflict') return prefix + 'this sub-batch changed elsewhere.';
  return null;
}

type Pending = {intent: string; requestId: string};
function requestFor(slot: {current: Pending | null}, intent: string): string {
  const prior = slot.current;
  const requestId = prior && prior.intent === intent ? prior.requestId : newPigRequestId();
  slot.current = {intent, requestId};
  return requestId;
}

export function usePigSubBatches({
  feederGroups,
  setFeederGroups,
  getFeederGroups,
  applyFeederOp,
  reread,
  setNotice,
  confirmDelete,
  subAutoSaveTimer,
  pigAutoSaveTimer,
  partitionDirtyRef,
}: UsePigSubBatchesDeps) {
  const [showSubForm, setShowSubForm] = useState<string | null>(null); // batchId or null
  const [subForm, setSubForm] = useState<PigSubBatchForm>({
    name: '',
    giltCount: 0,
    boarCount: 0,
    originalPigCount: 0,
    notes: '',
  });
  const [editSubId, setEditSubId] = useState<string | null>(null);
  const subFormRef = useRef(subForm);
  subFormRef.current = subForm;
  const [subConflict, setSubConflict] = useState<PigConflictState | null>(null);

  async function setSubStatus(batchId: string, subId: string, operation: 'sub_batch.archive' | 'sub_batch.reopen') {
    const outcome = await applyFeederOp({
      requestId: newPigRequestId(),
      operation,
      target: {feeder_id: batchId, sub_batch_id: subId},
    });
    const failed = failureMessage(outcome, operation === 'sub_batch.archive' ? 'Not archived: ' : 'Not reopened: ');
    if (failed) setNotice({kind: 'error', message: failed});
  }
  async function archiveSubBatch(batchId: string, subId: string) {
    await setSubStatus(batchId, subId, 'sub_batch.archive');
  }
  async function unarchiveSubBatch(batchId: string, subId: string) {
    await setSubStatus(batchId, subId, 'sub_batch.reopen');
  }

  // Persist a sub-batch from the form. Returns its id once CONFIRMED.
  //
  // NEW: sub_batch.create with {name, notes, sex, count}; the server derives
  // giltCount/boarCount/originalPigCount and refuses a count past the parent's
  // remaining gilts/boars. A newer add-form revision that arrived while the
  // first create was in flight is confirmed as a partition (count) plus an
  // update (name/notes) on the created identity.
  //
  // EDIT: sub_batch.update of name/notes only, against the persisted values.
  // Counts are rebalanced from the parent's partition editor.
  async function persistSubBatch(
    batchId: string,
    formState: PigSubBatchForm,
    currentSubId: string | null,
  ): Promise<string | null> {
    const subId = currentSubId || (newSubId.current ??= String(Date.now()));
    const parent = getFeederGroups().find((g) => g.id === batchId);
    const existing = parent?.subBatches?.find((s) => s.id === subId) ?? null;
    if (!parent || (currentSubId && !existing)) {
      setNotice({kind: 'error', message: 'This sub-batch is no longer available. Your edits have not been saved.'});
      return null;
    }
    const target = {feeder_id: batchId, sub_batch_id: subId};
    const count = formState.count === '' || formState.count == null ? 0 : parseInt(formState.count as string) || 0;
    if (!existing) {
      const change = {name: formState.name, notes: formState.notes || '', sex: formState.sex || 'Gilts', count};
      const requestId = requestFor(createRequest, JSON.stringify([target, change]));
      const outcome = await applyFeederOp({requestId, operation: 'sub_batch.create', target, change});
      const alreadyThere = outcome.status === 'rejected' && outcome.code === '23505';
      if (outcome.status !== 'saved' && !alreadyThere) {
        setNotice({kind: 'error', message: failureMessage(outcome, 'Not saved: ') || 'Not saved.'});
        return null;
      }
      if (alreadyThere) reread();
      createRequest.current = null;
      return subId;
    }
    if (!currentSubId && subCount(existing) !== count) {
      const requestId = requestFor(partitionRequest, JSON.stringify([target, subCount(existing), count]));
      const outcome = await applyFeederOp({
        requestId,
        operation: 'sub_batch.partition',
        target,
        expected: {count: subCount(existing)},
        change: {count},
      });
      const failed = failureMessage(outcome, 'Not saved: ');
      if (failed) {
        setNotice({kind: 'error', message: failed});
        return null;
      }
      partitionRequest.current = null;
    }
    const fields = (['name', 'notes'] as const).filter((field) =>
      field === 'name'
        ? (existing.name ?? '') !== formState.name.trim()
        : ((existing.notes as string | undefined) ?? '') !== (formState.notes || ''),
    );
    if (fields.length === 0) return subId;
    const change = Object.fromEntries(fields.map((f) => [f, f === 'name' ? formState.name : formState.notes || '']));
    const expected = expectedFrom(existing as Record<string, unknown>, fields);
    const requestId = requestFor(updateRequest, JSON.stringify([target, expected, change]));
    const outcome = await applyFeederOp({requestId, operation: 'sub_batch.update', target, expected, change});
    if (outcome.status === 'saved' || outcome.status === 'no_change') {
      updateRequest.current = null;
      setSubConflict(null);
      return subId;
    }
    if (outcome.status === 'conflict') {
      updateRequest.current = null;
      setSubConflict({
        conflicts: outcome.conflicts,
        labels: {name: 'Name', notes: 'Notes'},
        useLatest: async () => {
          reread();
          setSubConflict(null);
          // Take the other person's value for each conflicting field; keep the
          // rest of this draft and send it again.
          const merged = {...subFormRef.current} as Record<string, unknown>;
          for (const c of outcome.conflicts) merged[c.field] = c.current == null ? '' : String(c.current);
          const form = merged as PigSubBatchForm;
          setSubForm(form);
          autosave.update({batchId, form, id: subId});
        },
      });
      return null;
    }
    setNotice({kind: 'error', message: failureMessage(outcome, 'Not saved: ') || 'Not saved.'});
    return null;
  }

  // Validate a new sub-batch against parent's available gilt/boar pool.
  // Returns null when valid, or a string error message. Edit mode skips
  // validation (counts are locked). The server re-checks under the lock.
  function validateNewSub(batchId: string, formState: PigSubBatchForm): string | null {
    if (!formState.name || !formState.name.trim()) return 'Sub-batch name is required.';
    const c = formState.count === '' || formState.count == null ? 0 : parseInt(formState.count as string) || 0;
    if (c <= 0) return 'Count must be 1 or more.';
    const sex = formState.sex || 'Gilts';
    const g = feederGroups.find((x) => x.id === batchId);
    if (!g) return null;
    const parentTotal = sex === 'Boars' ? parseInt(g.boarCount as string) || 0 : parseInt(g.giltCount as string) || 0;
    const usedBy = (g.subBatches || []).reduce(
      (a, s) =>
        s.id === newSubId.current
          ? a
          : a + (sex === 'Boars' ? parseInt(s.boarCount as string) || 0 : parseInt(s.giltCount as string) || 0),
      0,
    );
    const remaining = Math.max(0, parentTotal - usedBy);
    if (c > remaining)
      return (
        'Only ' +
        remaining +
        ' ' +
        sex.toLowerCase() +
        ' available on parent batch (has ' +
        parentTotal +
        ', already used ' +
        usedBy +
        ').'
      );
    return null;
  }

  const newSubId = useRef<string | null>(null);
  const createRequest = useRef<Pending | null>(null);
  const updateRequest = useRef<Pending | null>(null);
  const partitionRequest = useRef<Pending | null>(null);
  const closing = useRef(false);
  const persistRef = useRef(persistSubBatch);
  persistRef.current = persistSubBatch;
  const validateRef = useRef(validateNewSub);
  validateRef.current = validateNewSub;
  type SubDraft = {batchId: string; form: PigSubBatchForm; id: string | null};
  const [autosave] = useState(() =>
    createConfirmedDraftAutosave<SubDraft>({
      debounceMs: 1500,
      validate: ({batchId, form, id}) => {
        const error = !form.name.trim()
          ? 'Sub-batch name is required.'
          : id
            ? null
            : validateRef.current(batchId, form);
        if (error) setNotice({kind: 'error', message: error});
        return !error;
      },
      persist: async ({batchId, form, id}) => {
        const savedId = await persistRef.current(batchId, form, id);
        if (!savedId) return false;
        if (!id) {
          setEditSubId(savedId);
          const created = getFeederGroups()
            .find((g) => g.id === batchId)
            ?.subBatches?.find((s) => s.id === savedId);
          // Preserve name/notes typed while the first create was in flight.
          setSubForm((latest) => ({
            ...latest,
            giltCount: Number(created?.giltCount) || 0,
            boarCount: Number(created?.boarCount) || 0,
            originalPigCount: Number(created?.originalPigCount) || 0,
          }));
        }
        return true;
      },
      onState: () => {},
    }),
  );
  useLayoutEffect(() => {
    autosave.resume();
    return () => autosave.suspend();
  }, [autosave]);
  useAppDraftGuard({
    ownerKey: 'pig-sub-batch',
    isUnsafeToLeave: () => autosave.isDirty(),
    settleBeforeLeave: () => autosave.flush(),
  });
  function updSub(batchId: string, k: string, v: unknown) {
    if (closing.current) return;
    setNotice(null);
    const next: PigSubBatchForm = {...subForm, [k]: v};
    setSubForm(next);
    autosave.update({batchId, form: next, id: editSubId});
  }
  async function closeSubForm(batchId: string) {
    if (closing.current) return;
    clearTimeout(subAutoSaveTimer.current);
    // An untouched blank add may be cancelled. A failed/invalid draft remains.
    if (!editSubId && !subForm.name.trim() && !autosave.isDirty()) {
      setShowSubForm(null);
      return;
    }
    closing.current = true;
    autosave.update({batchId, form: subForm, id: editSubId});
    const confirmed = await autosave.flush();
    closing.current = false;
    if (!confirmed) return;
    setNotice(null);
    setSubConflict(null);
    newSubId.current = null;
    setShowSubForm(null);
    setEditSubId(null);
    setSubForm({name: '', sex: 'Gilts', count: 0, notes: ''});
  }
  async function saveSubBatch(batchId: string) {
    await closeSubForm(batchId);
  }

  function deleteSubBatch(batchId: string, subId: string) {
    confirmDelete('Delete this sub-batch? This cannot be undone.', async () => {
      if (closing.current) return;
      closing.current = true;
      try {
        if (!(await autosave.flush())) return;
        const outcome = await applyFeederOp({
          requestId: newPigRequestId(),
          operation: 'sub_batch.delete',
          target: {feeder_id: batchId, sub_batch_id: subId},
        });
        const failed = failureMessage(outcome, 'Not deleted: ');
        if (failed) {
          setNotice({kind: 'error', message: failed});
          return;
        }
        if (showSubForm === batchId && editSubId === subId) {
          setShowSubForm(null);
          setEditSubId(null);
          setSubConflict(null);
          newSubId.current = null;
        }
      } finally {
        closing.current = false;
      }
    });
  }

  // ---- Partition (the count editor inside the parent's modal) -------------
  const partitionDrafts = useRef(new Map<string, {batchId: string; subId: string; count: number}>());
  const partitionBase = useRef(new Map<string, number>());
  const partitionRequests = useRef(new Map<string, Pending>());
  const partitionFlight = useRef<Promise<boolean> | null>(null);

  function withPartitionDrafts(groups: PigFeederGroup[]): PigFeederGroup[] {
    if (partitionDrafts.current.size === 0) return groups;
    return groups.map((g) => {
      const subs = (g.subBatches || []).map((s) => {
        const draft = partitionDrafts.current.get(partitionKey(g.id as string, s.id as string));
        if (!draft) return s;
        const c = draft.count;
        return isBoarsSub(s)
          ? {...s, giltCount: 0, boarCount: c, originalPigCount: c}
          : {...s, giltCount: c, boarCount: 0, originalPigCount: c};
      });
      return {...g, subBatches: subs};
    });
  }

  // Sub's existing sex (giltCount-only or boarCount-only) is locked; this only
  // adjusts the magnitude. Uses pigAutoSaveTimer for the 1.5s debounce shared
  // with the parent's other fields.
  function updSubPartition(batchId: string, subId: string, newCountStr: number | string | null | undefined) {
    const c = Math.max(0, newCountStr === '' || newCountStr == null ? 0 : parseInt(newCountStr as string) || 0);
    const key = partitionKey(batchId, subId);
    if (!partitionBase.current.has(key)) {
      const sub = getFeederGroups()
        .find((g) => g.id === batchId)
        ?.subBatches?.find((s) => s.id === subId);
      if (!sub) return;
      partitionBase.current.set(key, subCount(sub));
    }
    partitionDrafts.current.set(key, {batchId, subId, count: c});
    setFeederGroups(withPartitionDrafts(getFeederGroups()));
    partitionDirtyRef.current = true;
    clearTimeout(pigAutoSaveTimer.current);
    pigAutoSaveTimer.current = setTimeout(() => void flushPartition(), 1500);
  }

  async function sendPartitions(): Promise<boolean> {
    const pending = [...partitionDrafts.current.entries()]
      .map(([key, d]) => ({key, ...d, base: partitionBase.current.get(key) ?? d.count}))
      // Decreases first: a rebalance must never over-allocate in between.
      .sort((a, b) => a.count - a.base - (b.count - b.base));
    let ok = true;
    for (const p of pending) {
      if (p.count === p.base) {
        partitionDrafts.current.delete(p.key);
        partitionBase.current.delete(p.key);
        continue;
      }
      const target = {feeder_id: p.batchId, sub_batch_id: p.subId};
      const intent = JSON.stringify([target, p.base, p.count]);
      const prior = partitionRequests.current.get(p.key);
      const requestId = prior && prior.intent === intent ? prior.requestId : newPigRequestId();
      partitionRequests.current.set(p.key, {intent, requestId});
      const outcome = await applyFeederOp({
        requestId,
        operation: 'sub_batch.partition',
        target,
        expected: {count: p.base},
        change: {count: p.count},
      });
      if (outcome.status === 'saved' || outcome.status === 'no_change') {
        partitionRequests.current.delete(p.key);
        partitionBase.current.set(p.key, p.count);
        // A newer edit typed during the flight stays pending against the new base.
        if (partitionDrafts.current.get(p.key)?.count === p.count) {
          partitionDrafts.current.delete(p.key);
          partitionBase.current.delete(p.key);
        }
      } else if (outcome.status === 'conflict') {
        // Someone else changed this count: their value stands, this edit is
        // dropped and said so (it was made against a count that no longer is).
        partitionRequests.current.delete(p.key);
        partitionDrafts.current.delete(p.key);
        partitionBase.current.delete(p.key);
        const now = outcome.conflicts[0]?.current;
        setNotice({
          kind: 'error',
          message: `Not saved: this sub-batch's count changed elsewhere${now == null ? '' : ' to ' + String(now)}.`,
        });
        ok = false;
      } else {
        setNotice({kind: 'error', message: failureMessage(outcome, 'Count not saved: ') || 'Count not saved.'});
        ok = false;
        break;
      }
      // Re-apply what is still pending over the adopted server rows.
      setFeederGroups(withPartitionDrafts(getFeederGroups()));
    }
    partitionDirtyRef.current = partitionDrafts.current.size > 0;
    return ok && partitionDrafts.current.size === 0;
  }

  /** Send every pending partition edit now. Resolves true once none is pending. */
  async function flushPartition(): Promise<boolean> {
    clearTimeout(pigAutoSaveTimer.current);
    while (partitionFlight.current) await partitionFlight.current;
    if (partitionDrafts.current.size === 0) {
      partitionDirtyRef.current = false;
      return true;
    }
    const flight = sendPartitions();
    partitionFlight.current = flight;
    try {
      return await flight;
    } finally {
      partitionFlight.current = null;
    }
  }

  return {
    showSubForm,
    setShowSubForm,
    subForm,
    setSubForm,
    editSubId,
    setEditSubId: (id: string | null) => {
      if (id === null && !autosave.isDirty()) newSubId.current = null;
      setEditSubId(id);
    },
    subConflict,
    archiveSubBatch,
    unarchiveSubBatch,
    validateNewSub,
    updSub,
    closeSubForm,
    saveSubBatch,
    deleteSubBatch,
    updSubPartition,
    flushPartition,
  };
}
